import { useRef, forwardRef, useState, useEffect, useImperativeHandle, useCallback } from "react";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import { Autocomplete, Popper, TextField, autocompleteClasses, Chip } from "@mui/material";
import jwtDecode from "jwt-decode";
import { styled } from "@mui/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SourceIcon from '@mui/icons-material/Source';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, FormControl, Grid, IconButton, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useTheme } from "@mui/material/styles";

const StyledPopper = styled(Popper)(({ theme }) => ({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: '11px',

        '& li': {
            padding: '3px 8px',
        },
    },
}));

export const rangeEditor = forwardRef((props, ref) => {
    const theme = useTheme();
    const [range, setRange] = useState(props.value || '')
    const [rangeOptions, setRangeOptions] = useState([]);
    const [vendId, setVendId] = useState(props?.data?.vendorNum);
    const [rangeId, setRangeId] = useState(props?.data?.rangeId);
    const [priceGroup, setPriceGroup] = useState(props?.data?.priceGroup);
    const refInput = useRef();
    const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRanges}`;

    const getRangeList = async () => {
        try {
            OAuthService.getList(`${rangeUrl}?vendorId=${vendId}`)
                .then((list) => {
                    const ranges = list.data;
                    setRangeOptions(
                        ranges?.map((range) => ({
                            value: range?.productName,
                            label: `${range?.priceGroup?.pgId}-${range?.productName}`,
                            id: range?.productId,
                            priceGroup: range?.priceGroup?.pgId,
                        }))
                    );
                })
                .catch();
        } catch (error) {
        }
    }

    useEffect(() => {
        getRangeList();
    }, []);

    const handleCustomerChange = (e, data) => {
   
    if (!data || typeof data !== 'object' || !data.id) {
      return;
    }
    const isRangeActuallyChanged = data.id !== rangeId;
    data.label && setRange(data.label);
    data.id && setRangeId(data.id);
    data.priceGroup && setPriceGroup(data.priceGroup);
    const rowData = props.node;
    data.label && rowData.setDataValue('range', data.label)
    data.id && rowData.setDataValue('rangeId', data.id)
    data.priceGroup && rowData.setDataValue('priceGroup', data.priceGroup)
    if (isRangeActuallyChanged) {
      rowData.setDataValue('color', '');
      rowData.setDataValue('colorId', '');
    }
};

    useEffect(() => {
        // get ref from React component
        window.setTimeout(() => {
            const eInput = refInput.current;
            eInput.focus();
        });
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                const rowNode = props.node;
                rowNode.setDataValue('range', range);
                rowNode.setDataValue('rangeId', rangeId)
                rowNode.setDataValue('priceGroup', priceGroup)

                return range;
            },
            getRangeId() {
                return rangeId;
            },
            focusIn() {
                var eInput = refInput.current;
                eInput.focus();
                eInput.select();
            },
        }
    });

    return (
        <Autocomplete
            options={rangeOptions}
            value={(range && rangeOptions.length > 0) ? (rangeOptions.filter(obj => obj?.label?.toLowerCase() === range?.toLowerCase() || obj?.value?.toLowerCase() === range.toLowerCase() || obj?.id?.toLowerCase() === props?.data?.rangeId?.toLowerCase()) ? rangeOptions.filter(obj => obj?.label?.toLowerCase() === range?.toLowerCase() || obj?.value?.toLowerCase() === range.toLowerCase() || obj?.id?.toLowerCase() === props?.data?.rangeId?.toLowerCase())[0] : '') : ''}
            onChange={handleCustomerChange}
            onInputChange={handleCustomerChange}
            disableClearable
            PopperComponent={StyledPopper}
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'Select '}
                    inputRef={refInput}
                    className="customDatePickerDiscount"
                    size="small"
                    sx={{ border: !range && `1px solid ${theme.palette.error.main}`, fontSize: '12px', backgroundColor: theme.palette.background.paper }}
                />
            )}
        />
    );
});

export const carcassEditor = forwardRef((props, ref) => {
    const [carcass, setCarcass] = useState(props.value || '')
    const [carcassOptions, setCarcassOptions] = useState([]);
    const [vendId, setVendId] = useState(props?.data?.vendorNum);
    const [carcassId, setCarcassId] = useState(props?.data?.carcassId);
    const refInput = useRef();
    const theme = useTheme();
    const carcassUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllCarcassByVendor}`;

    const getCarcassList = async () => {
        try {
            OAuthService.getList(`${carcassUrl}?vendorId=${vendId}`)
                .then((list) => {
                    const { carcasses } = list.data._embedded;
                    setCarcassOptions(
                        carcasses?.map((carcass) => ({
                            value: carcass.productId,
                            label: `${carcass.productId}-${carcass.productName}`,
                        }))
                    );
                })
                .catch();
        } catch (error) {
        }
    }

    useEffect(() => {
        getCarcassList();
    }, []);

    const handleCustomerChange = (e, data) => {
        data.label && setCarcass(data.label);
        data.value && setCarcassId(data.value);
        const rowData = props.node;
        data.label && rowData.setDataValue('carcass', data.label)
        data.value && rowData.setDataValue('carcassId', data.value)
    };

    useEffect(() => {
        // get ref from React component
        window.setTimeout(() => {
            const eInput = refInput.current;
            eInput.focus();
        });
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                const rowNode = props.node;
                rowNode.setDataValue('carcass', carcass);
                rowNode.setDataValue('carcassId', carcassId);

                return carcass;
            },
            getCarcassId() {
                return carcassId;
            },
            focusIn() {
                var eInput = refInput.current;
                eInput.focus();
                eInput.select();
            },
        }
    });

    return (
        <Autocomplete
            options={carcassOptions}
            value={(carcass && carcassOptions.length > 0) ? (carcassOptions.filter(obj => obj?.label?.toLowerCase() === carcass?.toLowerCase() || obj?.value?.toLowerCase() === carcassId?.toLowerCase()) ? carcassOptions.filter(obj => obj?.label?.toLowerCase() === carcass?.toLowerCase() || obj?.value?.toLowerCase() === carcassId?.toLowerCase())[0] : '') : ''}
            onChange={handleCustomerChange}
            onInputChange={handleCustomerChange}
            disableClearable
            PopperComponent={StyledPopper}
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'Select '}
                    inputRef={refInput}
                    className="customDatePickerDiscount"
                    size="small"
                    sx={{ border: !carcass && `1px solid ${theme.palette.error.main}`, fontSize: '12px', backgroundColor: theme.palette.background.paper }}
                />
            )}
        />
    );
});

export const handleEditor = forwardRef((props, ref) => {
    const [handle, setHandle] = useState(props.value || '')
    const [handleOptions, setHandleOptions] = useState([]);
    const [vendId, setVendId] = useState(props?.data?.vendorNum);
    const [handleId, setHandleId] = useState(props?.data?.handleId);
    const refInput = useRef();
    const theme = useTheme();
    const handleUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllHandlesByVendor}`;

    const getHandleList = async () => {
        try {
            OAuthService.getList(`${handleUrl}?vendorId=${vendId}`)
                .then((list) => {
                    const handles = list.data?._embedded?.handles;
                    setHandleOptions(
                        handles?.map((handle) => ({
                            value: handle.productId,
                            label: `${handle.productId}-${handle.productName}`,
                        }))
                    );
                })
                .catch();
        } catch (error) {
        }
    }

    useEffect(() => {
        getHandleList();
    }, []);

    const handleCustomerChange = (e, data) => {
        data.label && setHandle(data.label);
        data.value && setHandleId(data.value);
        const rowData = props.node;
        data.label && rowData.setDataValue('handle', data.label)
        data.value && rowData.setDataValue('handleId', data.value)
    };

    useEffect(() => {
        // get ref from React component
        window.setTimeout(() => {
            const eInput = refInput.current;
            eInput.focus();
        });
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                const rowNode = props.node;
                rowNode.setDataValue('handle', handle);
                rowNode.setDataValue('handleId', handleId);

                return handle;
            },
            getHandleId() {
                return handleId;
            },
            focusIn() {
                var eInput = refInput.current;
                eInput.focus();
                eInput.select();
            },
        }
    });

    return (
        <Autocomplete
            options={handleOptions}
            value={(handle && handleOptions.length > 0) ? (handleOptions.filter(obj => obj?.label?.toLowerCase() === handle?.toLowerCase() || obj?.value?.toLowerCase() === handleId?.toLowerCase()) ? handleOptions.filter(obj => obj?.label?.toLowerCase() === handle?.toLowerCase() || obj?.value?.toLowerCase() === handleId?.toLowerCase())[0] : '') : ''}
            onChange={handleCustomerChange}
            onInputChange={handleCustomerChange}
            disableClearable
            PopperComponent={StyledPopper}
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'Select '}
                    inputRef={refInput}
                    className="customDatePickerDiscount"
                    size="small"
                    sx={{ border: !handle && `1px solid ${theme.palette.error.main}`, fontSize: '12px', backgroundColor: theme.palette.background.paper }}
                />
            )}
        />
    );
});

export const colorEditor = forwardRef((props, ref) => {
    const [color, setColor] = useState(props.value || '')
    const [colorOptions, setColorOptions] = useState([]);
    const [rangeId, setRangeId] = useState(props?.data?.rangeId);
    const [colorId, setColorId] = useState(props?.data?.colorId);
    const refInput = useRef();
    const theme = useTheme();
    const colorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;

    const getColorList = async () => {
        try {
            if (rangeId) {
                OAuthService.getList(colorUrl + `/search/findRangeByProductId?productId=${rangeId}`)
                    .then((list) => {
                        const rangeData = list.data;
                        rangeData?.id && setRangeId(rangeData?.id);
                        if (rangeData?.id) {
                            OAuthService.getList(`${colorUrl}/${rangeData?.id}/variations`)
                                .then((list) => {
                                    const rangeVariations = list.data?._embedded?.rangeVariations;
                                    let colors = [];
                                    if (rangeVariations.length > 0) {
                                        colors = rangeVariations?.map((variations) => ({
                                            value: variations.productId,
                                            label: `${variations.productId}-${variations.productName}`,
                                            name: variations.productName
                                        }));
                                    }
                                    setColorOptions(colors);
                                })
                                .catch();
                        }
                    })
                    .catch();
            } else {
                if (props?.data?.range) {
                    OAuthService.getList(colorUrl + `/search/findRangeByName?name=${props?.data?.range}`)
                        .then((list) => {
                            const rangeData = list.data;
                            rangeData?.id && setRangeId(rangeData?.id);
                            if (rangeData?.id) {
                                OAuthService.getList(`${colorUrl}/${rangeData?.id}/variations`)
                                    .then((list) => {
                                        const rangeVariations = list.data?._embedded?.rangeVariations;
                                        let colors = [];
                                        if (rangeVariations.length > 0) {
                                            colors = rangeVariations?.map((variations) => ({
                                                value: variations.productId,
                                                label: `${variations.productId}-${variations.productName}`,
                                                name: variations.productName
                                            }));
                                        }
                                        setColorOptions(colors);
                                    })
                                    .catch();
                            }
                        })
                        .catch();
                }
            }
        } catch (error) {
        }
    }

    useEffect(() => {
        getColorList();
    }, []);

    const handleCustomerChange = (e, data) => {
        data.label && setColor(data.label);
        data.value && setColorId(data.value);
        const rowData = props.node;
        data.label && rowData.setDataValue('color', data.label)
        data.value && rowData.setDataValue('colorId', data.value)
    };

    const handleLoadColor = () => {
        props.api?.getCellEditorInstances().map((editor) => {
            if (editor?.getRangeId) {
                setRangeId(editor.getRangeId());
            }
        })
    }

    useEffect(() => {
        // get ref from React component
        window.setTimeout(() => {
            const eInput = refInput.current;
            eInput.focus();
        });

        props.api?.getCellEditorInstances().map((editor) => {
            if (editor?.getRangeId) {
                setRangeId(editor.getRangeId());
            }
        })
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                const rowNode = props.node;
                rowNode.setDataValue('color', color);
                rowNode.setDataValue('colorId', colorId);

                return color;
            },
            getColorId() {
                return colorId;
            },
            focusIn() {
                var eInput = refInput.current;
                eInput.focus();
                eInput.select();
            },
        }
    });

    return (
        <Autocomplete
            options={colorOptions}
            value={(color && colorOptions.length > 0) ? (colorOptions.filter(obj => obj?.label?.toLowerCase() === color?.toLowerCase() || obj?.name.toLowerCase() === color?.toLowerCase() || obj?.value?.toLowerCase() === colorId?.toLowerCase()) ? colorOptions.filter(obj => obj?.label?.toLowerCase() === color?.toLowerCase() || obj?.name.toLowerCase() === color?.toLowerCase() || obj?.value?.toLowerCase() === colorId?.toLowerCase())[0] : '') : ''}
            onChange={handleCustomerChange}
            onInputChange={handleCustomerChange}
            onFocus={handleLoadColor}
            disableClearable
            PopperComponent={StyledPopper}
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'Select '}
                    inputRef={refInput}
                    className="customDatePickerDiscount"
                    size="small"
                    sx={{ border: !color && `1px solid ${theme.palette.error.main}`, fontSize: '12px', backgroundColor: theme.palette.background.paper }}
                />
            )}
        />
    );
});

export const GetReasonEditor = forwardRef((props, ref) => {
  const { data, colDef } = props;
  const refReasons = useRef();
  const theme = useTheme();
  const [reasons, setReasons] = useState([]);
  const aoRoReasonsUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.aoRoReasons}`;
  const [selectedReasons, setSelectedReasons] = useState(data?.reason || "");

  const getAoReason = async () => {
    try {
      const list = await OAuthService.getList(aoRoReasonsUrl + `?size=1000`);
      const items = list.data?._embedded?.aoRoReasons || [];
      setReasons(
        items.map((item) => ({
          value: item.reasonId,
          label: item.reason,
          imgUpldReqd: item.imgUpldReqd,
          postnNumReqd: item.postnNumReqd,
          subReasons: item.subReasons || [],
        }))
      );
    } catch (error) {}
  };

  useEffect(() => {
    getAoReason();
  }, []);

  const handleChangeReason = (e, option) => {
  if (!option) return;
  if (colDef.field === "reason") {
    const reasonChanged = option.label !== data?.reason;
    props.node.setData({
      ...data,
      imgUpldReqd: option.imgUpldReqd,
      postnNumReqd: option.postnNumReqd,
      subReasons: option.subReasons || [],
      subReason: reasonChanged ? "" : data?.subReason || "",
    });
  }
  setSelectedReasons(option.label);
};

  useImperativeHandle(ref, () => ({
    getValue() {
      return selectedReasons;
    },
  }));

  const currentValue =
    selectedReasons && reasons.length > 0
      ? reasons.find(
          (obj) => obj?.label?.toLowerCase() === selectedReasons?.toLowerCase()
        ) || null
      : null;

  return (
    <Autocomplete
      ref={refReasons}
      options={reasons}
      value={currentValue}
      onChange={handleChangeReason}
      disableClearable
      PopperComponent={StyledPopper}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select "
          className="customDatePickerDiscount"
          size="small"
          sx={{
            border: !selectedReasons && `1px solid ${theme.palette.error.main}`,
            fontSize: "12px",
            backgroundColor: theme.palette.background.paper,
          }}
        />
      )}
    />
  );
});

export const GetSubReasonEditor = forwardRef((props, ref) => {
  const { data } = props;
  const theme = useTheme();


  const options = Array.from(
    new Set([
      ...(data?.subReasons || []),
      ...(data?.subReason ? [data.subReason] : []),
    ])
  );
  const [selected, setSelected] = useState(data?.subReason || null);

  useImperativeHandle(ref, () => ({
    getValue() {
      return selected || "";
    },
  }));

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(e, option) => setSelected(option)}
      PopperComponent={StyledPopper}
      size="small"
      noOptionsText="No sub reasons"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select "
          className="customDatePickerDiscount"
          size="small"
          sx={{
            border: !selected && `1px solid ${theme.palette.error.main}`,
            fontSize: "12px",
            backgroundColor: theme.palette.background.paper,
          }}
        />
      )}
    />
  );
});

export const PictureEditorParams = (props) => {
    const { data, colDef } = props
    const [error, setError] = useState("");
    const hiddenPictureFile = useRef(null);
    const [pictureFile, setPictureFile] = useState(null)
    const uploadUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.uploadPicture}`;
    const imgUpldReqd = data.data.imgUpldReqd || false;
    const removePictureFile = () => {
        hiddenPictureFile.current.value = null;
        setPictureFile(null);
    };
    const handlePictureUpload = async (e) => {
        const fileUploaded = e.target.files[0];
        const formData = new FormData();
        formData.append("file", fileUploaded);
        const uploadPicture = await OAuthService.uploadFile(uploadUrl, formData);
        if (uploadPicture.data) {
            if (uploadPicture.data.error) {
                setError(uploadPicture.data.error);
            } else {
                const downloadUrl = uploadPicture.data.downloadUri;
                console.log('downloadUrl', downloadUrl)
            }
        }
        setPictureFile(fileUploaded);
    };

    return (
        imgUpldReqd && (
            <>
                <ColorButton
                    variant="contained"
                    component="label"
                    disabled={pictureFile}
                    style={{ padding: '1px 10px', width: '36px', margin: '0 auto', cursor: 'pointer' }}
                >
                    <FuseSvgIcon size={16}>heroicons-outline:upload</FuseSvgIcon>
                    <input
                        type="file"
                        className="file"
                        onChange={handlePictureUpload}
                        ref={hiddenPictureFile}
                        disabled={pictureFile}
                    />
                </ColorButton>
                {pictureFile && (
                    <Box className="mt-16">
                        <Chip
                            label={pictureFile.name || pictureFile.attachmentName}
                            onDelete={removePictureFile}
                        />
                    </Box>
                )}
            </>
        )
    );
};

export const uploadSingleFilePopup = forwardRef((props, ref) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const [documentFile, setDocumentFile] = useState([]);
    const [documentName, setDocumentName] = useState("");

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDocumentChange = (e) => {
        const file = e.target.files[0];
        setDocumentName(file.name);
        setDocumentFile(file);
        const rowData = props.node;
        rowData.setDataValue("documentPath", file.name);
    }

    useEffect(() => {
        if (props.data.documentPath) {
            setDocumentName(props.data.documentPath);
            // setDocumentFile(props.data.file ? props.data.file : []);
        }
    }, [props.data]);

    const removeDocument = () => {
        setDocumentName("");
        setDocumentFile([]);
        const rowData = props.node;
        rowData.setDataValue("documentPath", '');
    }

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                const rowData = props.node;
                rowData.setDataValue("documentFile", documentFile);
                return documentName;
            }
        };
    });



    return (
        <div>
            <IconButton onClick={handleClickOpen}><SourceIcon color="secondary" /></IconButton>
            <Dialog fullWidth maxWidth={'sm'}
                open={open}
                onClose={handleClose}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{"Upload documents"}</DialogTitle>
                <DialogContent>
                    {!documentName ?
                        <Stack className='w-full mb-16'>
                            <Box className='flex justify-center items-center'
                                sx={{

                                    height: 180,
                                    border: `1px dotted ${theme.palette.divider}`,
                                }}
                            >
                                <CloudUploadIcon fontSize={'large'} /><Typography className='ml-12' >Upload your image</Typography>
                                <Button
                                    variant="contained"
                                    component="label"
                                    color="secondary"
                                    className='ml-12'
                                >
                                    <input hidden type="file"
                                        onChange={(e) => handleDocumentChange(e)}
                                    />
                                    <Typography color="text.primary">Browse</Typography>
                                </Button>
                            </Box>
                        </Stack>
                        :
                        <Stack className='w-full mb-16'>

                            <Box
                                className='flex justify-between'
                            >
                                <Grid className='flex'>
                                    <div>
                                        <FileCopyIcon fontSize={'large'} color="secondary" className=' mt-20' />
                                    </div>
                                    <div>
                                        <Typography fontSize={'large'} className='ml-12 mt-20' >{documentName}</Typography>

                                    </div>
                                </Grid>
                                <Grid>
                                    <FuseSvgIcon className='mt-20 w-32 h-32 cursor-pointer' onClick={() => removeDocument()}>heroicons-outline:x</FuseSvgIcon>
                                </Grid>
                            </Box>
                        </Stack>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                    {/* <Button onClick={handleClose}>Uplaod</Button> */}
                </DialogActions>
            </Dialog>
        </div>
    )
})